/**
 * QuetureTestPage 상수 정의
 */
import type { QuetureTestLabels, PauseAndGoType } from './types'

/**
 * 빠른 선택용 Queture ID (PauseAndGo 유형별)
 *
 * - 다양한 조합: 여러 PAG 타입이 섞인 깨처
 * - 타입별: 특정 타입을 집중 확인할 수 있는 깨처
 */
export const QUETURE_IDS_PRESET: Record<string, string[]> = {
  '다양한 조합 (GO+QUIZ+AUTOGO+REPEAT)': [
    '2001972690453509', // 4타입: GO, AUTOGO, QUIZ, REPEAT (6개)
  ],
  'GO + QUIZ + AUTOGO (3타입)': [
    '2826730043380114', // 7개 PAG
    '2761735913902187', // 6개 PAG
    '2575621508861958', // 5개 PAG
  ],
  'GO + QUIZ + REPEAT (3타입)': [
    '2734423252176922', // 4개 PAG
  ],
  'GO + QUIZ (2타입)': [
    '2761762883276910',
    '2581260415045652',
    '1836434861360382', // 5개 PAG
  ],
  'QUIZ 단독': [
    '10372580608976023',
    '11553430688113755',
  ],
  'GO 단독': [
    '13237640918402413', // 3개 PAG
  ],
}

/** PauseAndGo 타입 한국어 라벨 */
export const PAUSE_AND_GO_TYPE_LABELS: Record<PauseAndGoType, string> = {
  PAUSE_AND_GO: '일시정지',
  PAUSE_AND_AUTOGO: '자동재개',
  PAUSE_AND_REPEAT: '반복확인',
  PAUSE_AND_QUIZ: '퀴즈',
  PAUSE_AND_VIDEO: '영상보기',
}

/** PauseAndGo 타입별 Tailwind 색상 클래스 */
export const PAUSE_AND_GO_TYPE_COLORS: Record<PauseAndGoType, string> = {
  PAUSE_AND_GO: 'bg-blue-100 text-blue-800',
  PAUSE_AND_AUTOGO: 'bg-green-100 text-green-800',
  PAUSE_AND_REPEAT: 'bg-yellow-100 text-yellow-800',
  PAUSE_AND_QUIZ: 'bg-purple-100 text-purple-800',
  PAUSE_AND_VIDEO: 'bg-pink-100 text-pink-800',
}

/** 한국어 기본 텍스트 (i18n 미제공 시 fallback) */
export const DEFAULT_LABELS: QuetureTestLabels = {
  enterQuetureId: 'Queture ID를 입력해주세요.',
  fetchFailed: '깨처를 불러오는데 실패했습니다. ID를 확인해주세요.',
  quickSelect: '빠른 선택',
  quetureIdLabel: 'Queture ID',
  quetureIdPlaceholder: 'Queture ID를 입력하세요',
  search: '조회',
  loading: '깨처를 불러오는 중...',
  summaryToggle: '반짝깨처 보기',
  pauseAndGoList: 'PauseAndGo 목록',
  time: '시간',
  type: '타입',
  bundleItem: '번들',
  status: '상태',
  message: '메시지',
  completed: '완료',
  notCompleted: '미완료',
  noData: 'PauseAndGo 데이터가 없습니다.',
  reset: '초기화',
  confirm: '확인',
  yes: '예',
  no: '아니오',
  autoResumeIn: '초 후 자동 재생',
  watchRelatedQueture: '관련 깨처를 보고 올까요?',
  skip: '건너뛰기',
  correct: '정답',
  incorrect: '오답',
  manualResult: '결과 선택',
  published: '발행',
  unpublished: '미발행',
  includeUnpublished: '미발행 PAG 포함',
  quizLoading: '퀴즈를 불러오는 중...',
  quizLoadFailed: '퀴즈를 불러오는데 실패했습니다.',
  solving: '채점 중...',
  pauseAndGoDefaultMsg: '잠시 멈추고 생각해보세요',
  repeatDefaultMsg: '이해했나요?',
  videoDefaultMsg: '관련 깨처를 보고 올까요?',
  quizApiNotConnected: '퀴즈 API가 연결되지 않았습니다.',
  quizIdRef: 'Quiz ID 참고',
  dismissNoComplete: '건너뛰기 (완료 처리 없음)',
}

/** 깨보드 관련 깨처 ID (카테고리별) - Curriculum Summary Oct 24 CSV 기반 */
export const QUEBOARD_QUETURE_GROUPS: Record<string, string[]> = {
  '곱하기 9, 합도 9': ['12576579475088874'],
  '8×8, 구구단 절대 외우지 마라': ['11744150405391536'],
  '텔레포트를 다이어그램으로': ['12576580901152237'],
  '쉬운 타깃네모를 골라라': ['12576572218942937'],
  '곱하기 7, 상대를 이용하라': ['12576579047269865'],
  '깨구단 종합편': ['12576567068337622'],
}

/**
 * 밀리초를 mm:ss 형식으로 변환
 */
export function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * 번역 함수로 라벨 생성 (없으면 DEFAULT_LABELS fallback)
 */
export function createLabels(t?: (key: string) => string): QuetureTestLabels {
  if (!t) return DEFAULT_LABELS

  const result = {} as Record<string, string>
  for (const key of Object.keys(DEFAULT_LABELS)) {
    result[key] = t(`quetureTest.${key}`) || DEFAULT_LABELS[key as keyof QuetureTestLabels]
  }
  return result as unknown as QuetureTestLabels
}
